/**
 * BadgeCelebration — imperative DOM overlay that shows when a new badge is unlocked.
 *
 * Mirrors the PRCelebration pattern (no React render cycle, fixed z-index, auto-dismiss)
 * but renders the badge icon + title + description + "Share" action.
 *
 * Usage:
 *   import { showBadgeCelebration } from '../components/BadgeCelebration';
 *   showBadgeCelebration({ icon: '🏆', title: 'Dedicated', description: 'Complete 100 workouts' }, { onShare: () => {} });
 */

const DURATION = 60000;

let activeOverlay = null;

function ensureKeyframes() {
  if (document.getElementById('badge-celebration-kf')) return;
  const style = document.createElement('style');
  style.id = 'badge-celebration-kf';
  style.textContent = `
    @keyframes badge-card-in {
      0% { opacity: 0; transform: translate(-50%, -50%) scale(0.5) rotate(-10deg); }
      60% { opacity: 1; transform: translate(-50%, -50%) scale(1.08) rotate(2deg); }
      100% { opacity: 1; transform: translate(-50%, -50%) scale(1) rotate(0deg); }
    }
    @keyframes badge-glow-pulse {
      0%, 100% { box-shadow: 0 0 50px 10px rgba(255,92,0,0.4); }
      50% { box-shadow: 0 0 80px 20px rgba(255,92,0,0.7); }
    }
    @keyframes badge-icon-float {
      0%, 100% { transform: translateY(0) rotate(0deg); }
      50% { transform: translateY(-6px) rotate(3deg); }
    }
    @keyframes badge-confetti-fall {
      0% { opacity: 1; transform: translate(0, 0) rotate(0deg); }
      100% { opacity: 0; transform: translate(var(--confetti-x, 0), 100dvh) rotate(720deg); }
    }
    @keyframes badge-backdrop-in {
      0% { opacity: 0; }
      100% { opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

function dismiss() {
  if (!activeOverlay) return;
  const overlay = activeOverlay;
  activeOverlay = null;
  overlay.style.transition = 'opacity 0.4s ease';
  overlay.style.opacity = '0';
  setTimeout(() => overlay.remove(), 400);
}

/**
 * @param {{ icon: string, title: string, description: string, category?: string }} badge
 * @param {{ onShare?: () => void, autoDismiss?: boolean }} [options]
 */
export function showBadgeCelebration(badge, options = {}) {
  ensureKeyframes();

  // Optional i18n: callers may pass `t` (react-i18next). Falls back to English.
  const T = typeof options.t === 'function' ? options.t : (_k, fallback) => fallback;
  const L = {
    unlocked: T('celebration.achievementUnlocked', 'Achievement Unlocked'),
    share: T('celebration.shareTitle', 'Share'),
    dismiss: T('celebration.dismissTitle', 'Dismiss'),
  };

  if (activeOverlay) {
    activeOverlay.remove();
    activeOverlay = null;
  }

  const overlay = document.createElement('div');
  activeOverlay = overlay;
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 9998;
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(165deg, rgba(10,10,10,0.92) 0%, rgba(26,10,0,0.92) 50%, rgba(5,5,5,0.94) 100%);
    animation: badge-backdrop-in 0.3s ease forwards;
  `;
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) dismiss();
  });

  // Confetti
  const colors = ['#FF5C00', '#FCD34D', '#FFFFFF', '#FF8C42', '#EAB308'];
  for (let i = 0; i < 24; i++) {
    const piece = document.createElement('div');
    const xOffset = (Math.random() - 0.5) * 600;
    piece.style.cssText = `
      position: absolute; top: -10px; left: 50%;
      width: ${6 + Math.random() * 6}px; height: ${8 + Math.random() * 10}px;
      background: ${colors[i % colors.length]};
      border-radius: 2px;
      animation: badge-confetti-fall ${2500 + Math.random() * 1500}ms ease-in forwards;
      animation-delay: ${Math.random() * 400}ms;
      --confetti-x: ${xOffset}px;
    `;
    overlay.appendChild(piece);
  }

  // Card
  const card = document.createElement('div');
  card.style.cssText = `
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: min(360px, 88vw);
    padding: 32px 24px 24px;
    border-radius: 24px;
    background: linear-gradient(155deg, rgba(40,20,5,0.95) 0%, rgba(15,15,20,0.97) 100%);
    border: 1px solid rgba(255,92,0,0.4);
    animation: badge-card-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards,
               badge-glow-pulse 2.2s ease-in-out infinite 0.6s;
    backdrop-filter: blur(12px);
    text-align: center;
    opacity: 0;
  `;

  const label = document.createElement('div');
  label.style.cssText = `
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 13px; font-weight: 700; letter-spacing: 3px;
    color: #FF5C00; text-transform: uppercase; margin-bottom: 8px;
  `;
  label.textContent = L.unlocked;
  card.appendChild(label);

  const icon = document.createElement('div');
  icon.style.cssText = `
    font-size: 88px; line-height: 1; margin: 12px 0;
    animation: badge-icon-float 2.4s ease-in-out infinite;
    filter: drop-shadow(0 0 20px rgba(255,92,0,0.6));
  `;
  icon.textContent = badge.icon || '🏆';
  card.appendChild(icon);

  const title = document.createElement('div');
  title.style.cssText = `
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 32px; font-weight: 800; color: #fff;
    text-transform: uppercase; letter-spacing: 1px; margin-top: 4px;
  `;
  title.textContent = badge.title;
  card.appendChild(title);

  const desc = document.createElement('div');
  desc.style.cssText = `
    font-size: 14px; color: rgba(255,255,255,0.75); margin-top: 8px;
  `;
  desc.textContent = badge.description;
  card.appendChild(desc);

  // Buttons
  const btnRow = document.createElement('div');
  btnRow.style.cssText = `display: flex; gap: 8px; margin-top: 20px; justify-content: center;`;

  if (options.onShare) {
    const shareBtn = document.createElement('button');
    shareBtn.style.cssText = `
      flex: 1; min-height: 44px; padding: 12px 16px; border-radius: 12px;
      background: #FF5C00; color: #fff; font-weight: 700;
      font-size: 14px; border: none; cursor: pointer;
      transition: transform 0.15s ease;
    `;
    shareBtn.textContent = L.share;
    shareBtn.onmouseenter = () => (shareBtn.style.transform = 'scale(1.03)');
    shareBtn.onmouseleave = () => (shareBtn.style.transform = 'scale(1)');
    shareBtn.onclick = () => {
      options.onShare();
      dismiss();
    };
    btnRow.appendChild(shareBtn);
  }

  const closeBtn = document.createElement('button');
  closeBtn.style.cssText = `
    flex: 1; min-height: 44px; padding: 12px 16px; border-radius: 12px;
    background: rgba(255,255,255,0.08); color: #fff; font-weight: 600;
    font-size: 14px; border: 1px solid rgba(255,255,255,0.12); cursor: pointer;
  `;
  closeBtn.textContent = L.dismiss;
  closeBtn.onclick = dismiss;
  btnRow.appendChild(closeBtn);

  card.appendChild(btnRow);
  overlay.appendChild(card);
  document.body.appendChild(overlay);

  if (options.autoDismiss !== false) {
    setTimeout(dismiss, DURATION);
  }
}

export function dismissBadgeCelebration() {
  dismiss();
}
